import type { CSSProperties } from "react";
import { ColorType, getColorValue } from "@/theme/colors";

/** The locked Flow tight budget (tweak xtuduo6): labels past this cut with
 *  `..` via truncateMarkdownSource, and the label flows as real inline text
 *  so it breaks where the LINE ends — never after the first word. */
export const REFERENCE_PILL_LABEL_LIMIT = 24;

/** Stable across every loading pill: the key seeds the decrypt origins, so a
 *  per-instance key would make each `[loading]` scramble differently. */
export const REFERENCE_PILL_LOADING_PLAY_KEY = "creference-loading-reversing-decrypt";

const PILL_LEADING_HOVERED_BOX_SHADOW =
  "#ffffffa8 1px 1px 3px 0px inset, #ffffff47 -1px -1px 2px -1px inset, #00000080 0px 2px 3px 0px, #ffffff 0 -2px 2px 0px inset";
const PILL_LEADING_IDLE_BOX_SHADOW =
  "#ffffffa8 1px 1px 3px 0px inset, #ffffff47 -1px -1px 2px -1px inset, #00000080 0px 2px 3px 0px, transparent 0 -2px 2px 0px inset";

interface CreferencePillRootStyleInput {
  disabled: boolean;
  isUnresolved: boolean;
  style?: CSSProperties;
}

interface CreferencePillLeadingStyleInput {
  isHovered: boolean;
  isMemberReference: boolean;
  memberColor: string | null;
}

interface CreferencePillLabelStyleInput {
  isHovered: boolean;
  linkedLabelTextStyle: CSSProperties;
}

export function getCreferencePillRootStyle({
  disabled,
  isUnresolved,
  style,
}: CreferencePillRootStyleInput): CSSProperties {
  return {
    display: "inline",
    verticalAlign: "baseline",
    opacity: disabled || isUnresolved ? 0.7 : 1,
    ...style,
  };
}

export function getCreferencePillInlineClusterStyle(): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    minWidth: 0,
    maxWidth: "100%",
  };
}

export function getCreferencePillLeadingStyle({
  isHovered,
  isMemberReference,
  memberColor,
}: CreferencePillLeadingStyleInput): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    verticalAlign: "middle",
    flexShrink: 0,
    margin: "0px 6px 3px 4px",
    padding: "5px",
    borderRadius: "20px",
    backgroundColor: getCreferencePillLeadingBackground({
      isMemberReference,
      memberColor,
    }),
    boxShadow: isHovered
      ? PILL_LEADING_HOVERED_BOX_SHADOW
      : PILL_LEADING_IDLE_BOX_SHADOW,
    transition: "box-shadow 0.3s ease",
  };
}

/** Inline-flow label boxes: the wrapper AND the compact paragraph become
 *  `display:inline`, so the label's words wrap with the line like ordinary
 *  prose instead of moving as one atomic inline-flex box. */
export function getCreferencePillInlineFlowWrapperStyle(): CSSProperties {
  return {
    display: "inline",
  };
}

export function getCreferencePillInlineFlowParagraphStyle(): CSSProperties {
  return {
    display: "inline",
    marginBottom: 0,
  };
}

export function getCreferencePillLabelTextStyle({
  isHovered,
  linkedLabelTextStyle,
}: CreferencePillLabelStyleInput): CSSProperties {
  return {
    textDecorationLine: "underline",
    textDecorationStyle: isHovered ? "solid" : "dotted",
    textUnderlineOffset: "3px",
    transition: "text-decoration-style 0.15s ease",
    ...linkedLabelTextStyle,
  };
}

export function getCreferencePillTextColor(isUnresolved: boolean): ColorType {
  return isUnresolved ? ColorType.White : ColorType.Accent;
}

export function getCreferencePillTextOpacity(isUnresolved: boolean): number {
  return isUnresolved ? 0.85 : 0.95;
}

function getCreferencePillLeadingBackground(input: {
  isMemberReference: boolean;
  memberColor: string | null;
}): string {
  if (input.isMemberReference && input.memberColor) {
    return `${input.memberColor}A6`;
  }

  return getColorValue(ColorType.Accent, 0.29);
}
